import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { Settings, Briefcase, Image, MessageSquare, LogOut, Menu, X } from 'lucide-react';

const AdminDashboardPage = () => {
    const navigate = useNavigate();
    const [showGallery, setShowGallery] = useState(true);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Estado para controlar a sidebar no mobile

    // Remove o preloader quando o componente é montado
    useEffect(() => {
        const preloader = document.getElementById('preloader');
        if (preloader) {
            preloader.style.display = 'none';
        }
    }, []);

    // Ouve as configurações do site para o menu dinâmico
    useEffect(() => {
        const docRef = doc(db, "siteContent", "main");
        const unsubscribe = onSnapshot(docRef, (doc) => {
            if (doc.exists()) {
                setShowGallery(doc.data().showGallery !== false);
            }
        });
        return () => unsubscribe();
    }, []);

    const handleLogout = async () => {
        await signOut(auth);
        navigate('/admin');
    };

    const baseNavItems = [
        { to: "/dashboard/content", icon: <Settings size={20} />, label: "Conteúdo e Aparência" },
        { to: "/dashboard/services", icon: <Briefcase size={20} />, label: "Serviços" },
        { to: "/dashboard/testimonials", icon: <MessageSquare size={20} />, label: "Depoimentos" },
    ];
    
    const navItems = showGallery
        ? [
            ...baseNavItems.slice(0, 2),
            { to: "/dashboard/gallery", icon: <Image size={20} />, label: "Galeria" },
            ...baseNavItems.slice(2)
          ]
        : baseNavItems;

    // Componente interno para o conteúdo da sidebar, para evitar repetição de código
    const SidebarContent = () => (
        <>
            <h1 className="text-2xl font-bold text-primary mb-10">Hulk</h1>
            <nav className="flex flex-col space-y-2 flex-grow">
                {navItems.map(item => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setIsSidebarOpen(false)} // Fecha o menu ao clicar em um link no mobile
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                            isActive
                                ? 'bg-primary text-button-text font-semibold'
                                : 'hover:bg-neutral-700'
                            }`
                        }
                    >
                        {item.icon}
                        <span>{item.label}</span>
                    </NavLink>
                ))}
            </nav>
            <button 
                onClick={handleLogout} 
                className="flex items-center w-full gap-3 px-4 py-2 mt-4 rounded-lg text-red-400 hover:bg-red-600/20 hover:text-red-300 transition-colors"
            >
                <LogOut size={20} />
                <span>Sair</span>
            </button>
        </>
    );

    return (
        <div className="min-h-screen bg-neutral-900 text-white">
            {/* Sidebar Fixa para Desktop */}
            <aside className="w-64 bg-neutral-800 p-6 flex-col border-r border-neutral-700/50 fixed inset-y-0 left-0 hidden md:flex">
                <SidebarContent />
            </aside>

            {/* Sidebar Deslizante para Mobile */}
            <aside className={`md:hidden fixed inset-y-0 left-0 w-64 bg-neutral-800 p-6 flex flex-col z-50 transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <button onClick={() => setIsSidebarOpen(false)} className="absolute top-4 right-4 text-white md:hidden">
                    <X size={24} />
                </button>
                <SidebarContent />
            </aside>

            {/* Conteúdo Principal */}
            <div className="md:ml-64 flex flex-col flex-1 h-screen">
                {/* Cabeçalho para Mobile com botão de menu */}
                <header className="md:hidden bg-neutral-800 p-4 flex items-center justify-between sticky top-0 z-30 border-b border-neutral-700/50">
                    <button onClick={() => setIsSidebarOpen(true)} className="text-white">
                        <Menu size={24} />
                    </button>
                    <h1 className="text-lg font-bold text-primary">Hulk</h1>
                </header>
                
                <main className="flex-1 p-6 sm:p-10 overflow-y-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default AdminDashboardPage;
